import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { usePortalInvoices, usePortalDownloadInvoice, type PortalInvoiceItem } from '@/hooks/usePortalInvoices';
import {
  useCreatePayCheckout,
  buildPayCheckoutReturnUrls,
  payReturnStorageKey,
} from '@/hooks/useClinicPay';
import { markPortalPaymentPending } from '@/lib/portalPaymentSettlement';
import { isCollectableInvoiceStatus } from '@/lib/invoiceDetailActions';
import { PortalPageHead } from '@/components/portal/PortalChrome';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { formatPortalKroner, formatPortalShortDate } from '@/lib/portalFormat';

const PORTAL_INVOICES_RETURN = '/portal/invoices';

export function PortalInvoices() {
  const { data: invoices = [], isLoading, error } = usePortalInvoices();
  const [tab, setTab] = useState<'all' | 'unpaid' | 'paid'>('all');

  const filtered = useMemo(() => {
    if (tab === 'unpaid') return invoices.filter((inv) => isCollectableInvoiceStatus(inv.status));
    if (tab === 'paid') return invoices.filter((inv) => inv.status === 'paid');
    return invoices;
  }, [invoices, tab]);

  const counts = useMemo(() => {
    return {
      all: invoices.length,
      unpaid: invoices.filter((inv) => isCollectableInvoiceStatus(inv.status)).length,
      paid: invoices.filter((inv) => inv.status === 'paid').length,
    };
  }, [invoices]);

  return (
    <>
      <PortalPageHead title="Fakturaer" subtitle="Se og betal dine fakturaer. Kvitteringen kommer på e-mail." />
      <div className="seg" role="group" aria-label="Filter" style={{ marginBottom: 16 }}>
        <button type="button" aria-pressed={tab === 'all'} onClick={() => setTab('all')}>
          Alle <span className="count">{counts.all}</span>
        </button>
        <button type="button" aria-pressed={tab === 'unpaid'} onClick={() => setTab('unpaid')}>
          Ubetalte <span className="count">{counts.unpaid}</span>
        </button>
        <button type="button" aria-pressed={tab === 'paid'} onClick={() => setTab('paid')}>
          Betalte
        </button>
      </div>
      {isLoading ? (
        <section className="card">
          <p className="help">Henter fakturaer…</p>
        </section>
      ) : error ? (
        <section className="card">
          <p className="help">Fakturaerne kunne ikke hentes. Prøv igen.</p>
        </section>
      ) : (
        <section className="card">
          <div className="inv hd">
            <div>Faktura</div>
            <div>Forfald</div>
            <div>Status</div>
            <div style={{ textAlign: 'right' }}>Beløb</div>
            <div />
          </div>
          {filtered.length === 0 ? (
            <p className="help">Ingen fakturaer i denne visning.</p>
          ) : (
            filtered.map((invoice) => <InvoiceRow key={invoice.id} invoice={invoice} />)
          )}
        </section>
      )}
      <div className="notice neutral" style={{ marginTop: 16 }}>
        <PortalIcon name="info" size="sm" />
        <span>
          Du betaler med kort eller MobilePay. Har du spørgsmål til en faktura, så{' '}
          <Link to="/portal/messages" className="link">
            skriv til klinikken
          </Link>
          .
        </span>
      </div>
    </>
  );
}

function InvoiceRow({ invoice }: { invoice: PortalInvoiceItem }) {
  const downloadMutation = usePortalDownloadInvoice();
  const createCheckout = useCreatePayCheckout();
  const [downloading, setDownloading] = useState(false);
  const unpaid = isCollectableInvoiceStatus(invoice.status);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const url = await downloadMutation.mutateAsync(invoice.id);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to download invoice:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handlePay = async () => {
    try {
      const result = await createCheckout.mutateAsync({
        ...(invoice.pay_token ? { pay_token: invoice.pay_token } : { invoice_id: invoice.id }),
        ...buildPayCheckoutReturnUrls(PORTAL_INVOICES_RETURN),
      });
      if (result.pay_token) {
        sessionStorage.setItem(payReturnStorageKey(result.pay_token), PORTAL_INVOICES_RETURN);
        markPortalPaymentPending({ payToken: result.pay_token });
      }
      window.location.href = result.checkout_url;
    } catch (err) {
      console.error('portal pay now failed', err);
      toast.error('Betalingen kunne ikke startes.');
    }
  };

  return (
    <div className="inv">
      <div>
        <div className="t">Faktura {invoice.invoice_number ?? invoice.id.slice(0, 8)}</div>
        <div className="s">{invoice.clinic_name}</div>
      </div>
      <div className="c-date num">
        {invoice.status === 'paid'
          ? invoice.paid_at
            ? `Betalt ${formatPortalShortDate(invoice.paid_at)}`
            : 'Betalt'
          : invoice.due_at
            ? formatPortalShortDate(invoice.due_at)
            : '—'}
      </div>
      <div className="c-st">
        <span className={`chip ${unpaid ? 'chip-amber' : 'chip-green'}`}>
          <PortalIcon name={unpaid ? 'clock' : 'check'} size="xs" />
          {unpaid ? 'Ubetalt' : 'Betalt'}
        </span>
      </div>
      <div className="amt num">{formatPortalKroner(invoice.total_cents)}</div>
      <div className="acts">
        <button type="button" className="btn btn-tertiary" onClick={() => void handleDownload()} disabled={downloading}>
          <PortalIcon name="download" size="sm" />
          PDF
        </button>
        {unpaid ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void handlePay()}
            disabled={createCheckout.isPending}
          >
            <PortalIcon name="card" size="sm" />
            Betal nu
          </button>
        ) : null}
      </div>
    </div>
  );
}
